import {
  useEffect,
  useId,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  BiChevronLeft,
  BiChevronRight,
  BiColumns,
  BiFilterAlt,
  BiSearch,
  BiSolidDownArrow,
  BiSolidUpArrow,
  BiSort,
  BiX,
} from 'react-icons/bi';
import type { PageMeta } from '@/types/shared';
import { ExportButton, type ExportButtonConfig } from '@/components/table/ExportButton';
import { FilterDrawer } from '@/components/table/FilterDrawer';
import { paginationWindow } from '@/components/table/pagination';
import './DataTable.css';

export interface DataTableColumn<T> {
  key: string;
  /** Already-translated header text (the table itself doesn't own column copy). */
  header: string;
  /** Server-side sort field name. Omit to make the column non-sortable — not every column a
   * module might like to sort by is necessarily supported by its list endpoint (see
   * `students.service.ts` `applySort` allow-list for the Admissions example). */
  sortField?: string;
  render: (row: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
  /** `false` excludes this column from the column-visibility panel and forces it always-rendered
   * (e.g. a primary identifier or the row-actions column). Default `true` — only consulted when
   * `columnVisibility` is configured; ignored entirely otherwise (fully backward compatible).
   * 14-tables-forms-restyle-spec.md §1.3/§4.2. */
  toggleable?: boolean;
}

export interface DataTableSortState {
  field: string | null;
  direction: 'asc' | 'desc';
}

export interface DataTableSearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
}

/** Column-visibility toggle config — 14-tables-forms-restyle-spec.md §4.2 (D-UI11). Omit to skip
 * the columns icon-button entirely. */
export interface DataTableColumnVisibilityConfig {
  /** localStorage key, unique per table instance on the page — D-UI11. */
  storageKey: string;
  /** aria-label for the toggle button, e.g. t('table.columns.toggleButtonAriaLabel'). */
  ariaLabel: string;
  /** Header text for the columns drawer, e.g. t('table.columns.drawerTitleDefault'). */
  drawerTitle: string;
}

/** Drives one removable pill in the active-filter chips row — 14-...-spec.md §3. */
export interface ActiveFilterChip {
  /** Stable key, e.g. 'grade' — used as the React key, never shown. */
  key: string;
  /** Already-translated filter group label, e.g. t('students.filters.grade'). */
  label: string;
  /** Already-translated, already-formatted value display, e.g. "Grade 6". */
  valueLabel: string;
  /** Clears just this one filter (and, per the existing per-page pattern, resets to page 1). */
  onRemove: () => void;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Accessible name for the table (visually hidden `<caption>`). */
  caption: string;
  meta: PageMeta | null;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  search?: DataTableSearchConfig;
  sort?: DataTableSortState;
  onSortChange?: (sort: DataTableSortState) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Toolbar slot for module-specific filters (grade/school/gender selects, etc). Rendered inside
   * the off-canvas `FilterDrawer` (14-...-spec.md §1.3/§2), unmodified — only *where* it renders
   * changed, not its content or behavior (still live-applies to the page's own state on change). */
  filtersSlot?: ReactNode;
  /** Filter drawer header text, e.g. reusing the page's existing `t('students.filters.groupLabel')`
   * copy. Defaults to the generic `table.filters.drawerTitleDefault` key when omitted. */
  filtersDrawerTitle?: string;
  /** Column-visibility toggle — omit to render no columns icon-button. */
  columnVisibility?: DataTableColumnVisibilityConfig;
  /** Drives the active-filter chips row (§3). Omit or pass [] to render no chips row at all. */
  activeFilters?: ActiveFilterChip[];
  /** Powers "Clear all" in both the chips row and the filter drawer's footer. Should be provided
   * whenever `activeFilters` or `filtersSlot` is provided (reuses each page's existing
   * `clearFilters()`). */
  onClearAllFilters?: () => void;
  /** Set `false` to omit the export button entirely (e.g. the Lookups settings screen, which has
   * no export endpoint and no business reason to offer one). Defaults to `true`. */
  showExportButton?: boolean;
  /** T75: wires the export button to a real `GET .../export` endpoint with the table's current
   * filters. Omit while `showExportButton` is `true` to keep the disabled "coming soon" stub
   * (10-design-system.md §4.1 — disabled + tooltip explaining why, for tables with no export
   * endpoint yet). */
  exportConfig?: ExportButtonConfig;
  emptyState: ReactNode;
  skeletonRowCount?: number;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Reads the persisted column-visibility map for `storageKey`, merged against the table's current
 * column keys — 14-...-spec.md §4.2/D-UI11. A column present today but absent from the stored map
 * defaults to visible; a stored key no longer present in `columnKeys` is discarded. Any read/parse
 * failure (corrupt JSON, storage disabled) falls back to "all visible". */
function loadColumnVisibility(storageKey: string, columnKeys: string[]): Record<string, boolean> {
  const allVisible = Object.fromEntries(columnKeys.map((key) => [key, true]));
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return allVisible;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return allVisible;
    const stored = parsed as Record<string, unknown>;
    const merged: Record<string, boolean> = {};
    for (const key of columnKeys) {
      merged[key] = typeof stored[key] === 'boolean' ? stored[key] : true;
    }
    return merged;
  } catch {
    return allVisible;
  }
}

function saveColumnVisibility(storageKey: string, value: Record<string, boolean>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Private-browsing/storage-disabled — column visibility just won't persist across sessions.
  }
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  meta,
  loading,
  error,
  onRetry,
  onRowClick,
  search,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  filtersSlot,
  filtersDrawerTitle,
  columnVisibility,
  activeFilters,
  onClearAllFilters,
  showExportButton = true,
  exportConfig,
  emptyState,
  skeletonRowCount = 6,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const reactId = useId();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);

  const columnKeysSignature = columns.map((column) => column.key).join('|');
  const [visibleColumnsMap, setVisibleColumnsMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!columnVisibility) return;
    setVisibleColumnsMap(
      loadColumnVisibility(columnVisibility.storageKey, columnKeysSignature.split('|')),
    );
    // Re-reads only when the storage key or the *set* of column keys changes, not on every
    // re-render (columns arrays are rebuilt inline by every list page each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibility?.storageKey, columnKeysSignature]);

  function toggleColumnVisibility(key: string) {
    if (!columnVisibility) return;
    setVisibleColumnsMap((prev) => {
      const next = { ...prev, [key]: prev[key] === false };
      saveColumnVisibility(columnVisibility.storageKey, next);
      return next;
    });
  }

  const toggleableColumns = columns.filter((column) => column.toggleable !== false);
  const visibleColumns = columnVisibility
    ? columns.filter(
        (column) => column.toggleable === false || visibleColumnsMap[column.key] !== false,
      )
    : columns;

  function handleHeaderClick(column: DataTableColumn<T>) {
    if (!column.sortField || !onSortChange) return;
    if (sort?.field === column.sortField) {
      onSortChange({
        field: column.sortField,
        direction: sort.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      onSortChange({ field: column.sortField, direction: 'asc' });
    }
  }

  function handleHeaderKeyDown(e: KeyboardEvent<HTMLButtonElement>, column: DataTableColumn<T>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleHeaderClick(column);
    }
  }

  function ariaSortFor(
    column: DataTableColumn<T>,
  ): 'ascending' | 'descending' | 'none' | undefined {
    if (!column.sortField) return undefined;
    if (sort?.field !== column.sortField) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  }

  function handleRowClick(e: MouseEvent<HTMLTableRowElement>, row: T) {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, select, textarea')) return;
    onRowClick(row);
  }

  const hasToolbar = Boolean(search || filtersSlot || columnVisibility || showExportButton);
  const activeFilterCount = activeFilters?.length ?? 0;
  const hasChips = activeFilterCount > 0;
  const showSkeleton = loading;
  const showEmpty = !loading && !error && rows.length === 0;
  const showRows = !loading && !error && rows.length > 0;

  return (
    <div className="fa-data-table">
      {hasToolbar && (
        <div className="fa-data-table__toolbar">
          {search && (
            <label className="fa-data-table__search">
              <span className="fa-visually-hidden">{search.ariaLabel}</span>
              <BiSearch className="fa-data-table__search-icon" aria-hidden="true" />
              <input
                type="search"
                value={search.value}
                placeholder={search.placeholder}
                onChange={(e) => search.onChange(e.target.value)}
              />
            </label>
          )}

          <div className="fa-data-table__toolbar-actions">
            {columnVisibility && (
              <button
                type="button"
                className={`fa-icon-btn${columnsOpen ? ' fa-icon-btn--open' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={columnsOpen}
                aria-label={columnVisibility.ariaLabel}
                onClick={() => setColumnsOpen(true)}
              >
                <BiColumns aria-hidden="true" />
              </button>
            )}

            {filtersSlot && (
              <button
                type="button"
                className={`fa-icon-btn${filtersOpen ? ' fa-icon-btn--open' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={filtersOpen}
                aria-label={
                  activeFilterCount > 0
                    ? t('table.filters.openButtonAriaLabelWithCount', { count: activeFilterCount })
                    : t('table.filters.openButtonAriaLabel')
                }
                onClick={() => setFiltersOpen(true)}
              >
                <BiFilterAlt aria-hidden="true" />
                {activeFilterCount > 0 && (
                  <span className="fa-icon-btn__badge" aria-hidden="true">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}

            {showExportButton && (
              <div className="fa-data-table__export">
                <ExportButton config={exportConfig} />
              </div>
            )}
          </div>
        </div>
      )}

      {hasChips && (
        <div className="fa-data-table__chips">
          {activeFilters!.map((chip) => (
            <span className="fa-data-table__chip" key={chip.key}>
              <strong>{chip.label}</strong>: <bdi>{chip.valueLabel}</bdi>
              <button
                type="button"
                className="fa-data-table__chip-remove"
                aria-label={t('table.filters.chipRemoveAriaLabel', { label: chip.label })}
                onClick={chip.onRemove}
              >
                <BiX aria-hidden="true" />
              </button>
            </span>
          ))}
          {onClearAllFilters && (
            <button
              type="button"
              className="fa-data-table__chips-clear"
              onClick={onClearAllFilters}
            >
              {t('table.filters.clearAll')}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="fa-data-table__error" role="alert">
          <p>{error}</p>
          {onRetry && (
            <button type="button" className="fa-btn fa-btn--secondary" onClick={onRetry}>
              {t('common.retry')}
            </button>
          )}
        </div>
      )}

      <div className="fa-data-table__scroll">
        <table className="fa-data-table__table">
          <caption className="fa-visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={column.headerClassName}
                  aria-sort={ariaSortFor(column)}
                >
                  {column.sortField ? (
                    <button
                      type="button"
                      className="fa-data-table__sort-btn"
                      onClick={() => handleHeaderClick(column)}
                      onKeyDown={(e) => handleHeaderKeyDown(e, column)}
                    >
                      <span>{column.header}</span>
                      <span className="fa-data-table__sort-icon" aria-hidden="true">
                        {sort?.field === column.sortField ? (
                          sort.direction === 'asc' ? (
                            <BiSolidUpArrow />
                          ) : (
                            <BiSolidDownArrow />
                          )
                        ) : (
                          <BiSort />
                        )}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} aria-hidden="true">
                  {visibleColumns.map((column) => (
                    <td key={column.key}>
                      <span className="fa-data-table__skeleton-block" />
                    </td>
                  ))}
                </tr>
              ))}

            {showEmpty && (
              <tr>
                <td colSpan={visibleColumns.length} className="fa-data-table__empty-cell">
                  {emptyState}
                </td>
              </tr>
            )}

            {showRows &&
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? 'fa-data-table__row--clickable' : undefined}
                  onClick={onRowClick ? (e) => handleRowClick(e, row) : undefined}
                >
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      data-label={column.header}
                      className={column.cellClassName}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {meta && !error && (
        <DataTablePagination
          meta={meta}
          pageSizeOptions={pageSizeOptions}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      {filtersSlot && (
        <FilterDrawer
          open={filtersOpen}
          id={`${reactId}-filters`}
          title={filtersDrawerTitle || t('table.filters.drawerTitleDefault')}
          onClose={() => setFiltersOpen(false)}
          closeAriaLabel={t('table.filters.close')}
          footer={
            <>
              {onClearAllFilters && (
                <button
                  type="button"
                  className="fa-btn fa-btn--secondary"
                  onClick={onClearAllFilters}
                >
                  {t('table.filters.clearAll')}
                </button>
              )}
              <button
                type="button"
                className="fa-btn fa-btn--primary"
                onClick={() => setFiltersOpen(false)}
              >
                {t('table.filters.done')}
              </button>
            </>
          }
        >
          {filtersSlot}
        </FilterDrawer>
      )}

      {columnVisibility && (
        <FilterDrawer
          open={columnsOpen}
          id={`${reactId}-columns`}
          title={columnVisibility.drawerTitle}
          onClose={() => setColumnsOpen(false)}
          closeAriaLabel={t('table.filters.close')}
          footer={
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              onClick={() => setColumnsOpen(false)}
            >
              {t('table.columns.done')}
            </button>
          }
        >
          <div className="fa-data-table__columns-list">
            {toggleableColumns.map((column) => {
              const checked = visibleColumnsMap[column.key] !== false;
              return (
                <label key={column.key} className="fa-data-table__columns-row">
                  <span>{column.header}</span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={checked}
                    onChange={() => toggleColumnVisibility(column.key)}
                  />
                </label>
              );
            })}
          </div>
        </FilterDrawer>
      )}
    </div>
  );
}

interface DataTablePaginationProps {
  meta: PageMeta;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function DataTablePagination({
  meta,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: DataTablePaginationProps) {
  const { t } = useTranslation();
  const { page, pageSize, totalPages, total } = meta;
  const pages = paginationWindow(page, totalPages);

  return (
    <div className="fa-data-table__footer">
      <nav className="fa-data-table__pagination" aria-label={t('table.pagination.navLabel')}>
        <button
          type="button"
          className="fa-data-table__page-btn fa-data-table__page-btn--prev"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('table.pagination.previous')}
        >
          <BiChevronLeft className="fa-data-table__page-icon" aria-hidden="true" />
          <span>{t('table.pagination.previous')}</span>
        </button>

        {pages.map((entry, idx) =>
          entry === null ? (
            <span key={`ellipsis-${idx}`} className="fa-data-table__ellipsis" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className="fa-data-table__page-btn"
              aria-current={entry === page ? 'page' : undefined}
              aria-label={t('table.pagination.goToPage', { page: entry })}
              onClick={() => onPageChange(entry)}
              disabled={entry === page}
            >
              {entry}
            </button>
          ),
        )}

        <button
          type="button"
          className="fa-data-table__page-btn fa-data-table__page-btn--next"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('table.pagination.next')}
        >
          <span>{t('table.pagination.next')}</span>
          <BiChevronRight className="fa-data-table__page-icon" aria-hidden="true" />
        </button>
      </nav>

      <div className="fa-data-table__meta">
        <span>{t('table.pagination.totalCount', { count: total })}</span>
        <label className="fa-data-table__page-size">
          <span>{t('table.pagination.rowsPerPage')}</span>
          <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
